import { Canvas, Image, Rect } from 'fabric/fabric-impl';
import { SCALE_ZOOM, STATE_IDLE, STATE_PANNING } from '../constants';
import { fabric } from 'fabric-with-gestures';
import { TextFormatSettings } from '../store/createStore';

import footerImg from '../assets/images/logo-bpp-mini.svg';

interface WorkArea {
  height: number;
  width: number;
}

export class CanvasHandler {
  constructor(public canvas: Canvas) {
    fabric.Object.prototype.cornerStyle = 'circle';
    fabric.Object.prototype.cornerColor = '#e89e9e';
    fabric.Object.prototype.borderColor = 'rgba(59, 56, 54, 0.5)';
    fabric.Object.prototype.transparentCorners = false;
    fabric.Object.prototype.cacheProperties = [
      'fill',
      'stroke',
      'strokeWidth',
      'strokeDashArray',
      'width',
      'height',
      'paintFirst',
      'strokeUniform',
      'strokeLineCap',
      'strokeDashOffset',
      'strokeLineJoin',
      'strokeMiterLimit',
      'backgroundColor',
      'clipPath',
      'globalCompositeOperation',
    ];
  }

  /**
   * Ajusta las dimensiones del canvas y su área de trabajo respecto a las dimensiones del área de trabajo misma
   * @param workArea Dimensiones del área de trabajo dentro del canvas
   * @param centerView
   */
  public async resize(workArea: WorkArea, centerView: boolean = true): Promise<any> {
    // @ts-ignore
    const canvasParentElem = this.canvas.wrapperEl;

    // Ajusta las dimensiones del canvas respecto a su contenedor
    this.canvas.setDimensions({ height: '100%', width: '100%' }, { cssOnly: true });
    this.canvas.setWidth(canvasParentElem.clientWidth);
    this.canvas.setHeight(canvasParentElem.clientHeight);

    if (centerView) {
      this.centerViewPort((this.canvas.height as number) / workArea.height - SCALE_ZOOM);
    }

    // Ajusta las posición del área de trabajo respecto a las nuevas dimensiones del canvas anteriormente calculadas
    const widthCanvas = this.canvas?.width as number;
    const heightCanvas = this.canvas?.height as number;
    const topCenter = (heightCanvas - workArea.height) / 2;
    const leftCenter = (widthCanvas - workArea.width) / 2;

    if (this.canvas.backgroundImage) {
      // Este bloque permite que los objetos ubicados en el canvas se reubiquen y mantengan su posición
      // respecto al área de trabajo, cuando el canvas se ve afectado por un reajuste de las dimensiones
      // del navegador (resize)
      const bg = this.canvas.backgroundImage as Image;

      const objects = this.canvas.getObjects();
      objects.forEach((object) => {
        // @ts-ignore
        object.set({
          // @ts-ignore
          left: object.left + leftCenter - bg.left,
          // @ts-ignore
          top: object.top + topCenter - bg.top,
        });
        object.setCoords();
      });

      bg.set({ left: leftCenter, top: topCenter });
      bg.setCoords();
    } else {
      const bg: Rect = new fabric.Rect({
        width: workArea.width,
        height: workArea.height,
        fill: 'white',
        selectable: false,
        objectCaching: false,
        evented: false,
        dirty: true,
        absolutePositioned: true,
        top: topCenter,
        left: leftCenter,
      });
      bg.canvas = this.canvas as Canvas;
      (this.canvas as Canvas).backgroundImage = bg as fabric.Image;
      //this.canvas.clipPath = bg;
      //this.canvas.controlsAboveOverlay = true;
    }

    // Configura overlay con ventana para visualizar el área de trabajo y el área que está fuera de de ser descargada
    const overlay1: Rect = new fabric.Rect({
      width: workArea.width,
      height: workArea.height,
      fill: 'white',
      borderColor: 'white',
      selectable: false,
      objectCaching: false,
      evented: false,
      dirty: true,
      absolutePositioned: true,
      top: topCenter,
      left: leftCenter,
      globalCompositeOperation: 'source-over',
      lockMovementX: true,
      lockMovementY: true,
    });
    const overlay2: Rect = new fabric.Rect({
      width: 10000,
      height: 10000,
      fill: 'rgba(76,74,71,0.2)',
      selectable: false,
      objectCaching: false,
      evented: false,
      dirty: true,
      left: (widthCanvas - 10000) / 2,
      top: (heightCanvas - 10000) / 2,
    });
    const logoFooterImg = await (async () =>
      new Promise<any>((resolve) => {
        try {
          fabric.Image.fromURL(footerImg, (image) => {
            image.set({
              left: leftCenter + workArea.width - 360,
              top: topCenter + workArea.height - 52,
            });
            resolve(image);
          });
        } catch (e) {
          resolve(new fabric.Image());
        }
      }))();
    const footer1: Rect = new fabric.Rect({
      width: workArea.width,
      height: 52,
      fill: '#255aa8',
      selectable: false,
      objectCaching: false,
      evented: false,
      dirty: true,
      left: leftCenter,
      top: topCenter + workArea.height - 52,
      globalCompositeOperation: 'source-over',
    });
    const workAreaGroupOverlay = new fabric.Group([overlay2, overlay1] as any, {
      selectable: false,
      objectCaching: false,
      evented: false,
      lockMovementX: true,
      lockMovementY: true,
    });

    /**
     * Cualquier modificación sobre el overlay que implique alterar esta configuración,
     * debe tener en cuenta que hay una configuración adicional que complementa a esta en el archivo
     * util/canvasHistory.ts, líneas 181 a la 185.
     */
    // @ts-ignore
    if (!this.canvas.bgOverlay) {
      // Después de agregar la primera vez el overlay sobre el área de trabajo, se evita tener nuevas
      // inserciones del mismo overlay en un futuro resize
      // @ts-ignore
      this.canvas.bgOverlay = workAreaGroupOverlay;
      this.canvas.add(workAreaGroupOverlay as any);
    }

    if (APP_CONF.SHOW_FOOTER_BPP) {
      const footerGroup = new fabric.Group([footer1, logoFooterImg], {
        objectCaching: false,
        selectable: false,
        evented: false,
      });

      // @ts-ignore
      this.canvas.setOverlayImage(footerGroup, this.canvas.renderAll.bind(this.canvas));
    }

    // Renderiza todos los cambios aplicados
    this.canvas.renderAll();

    return Promise.resolve();
  }

  /**
   * Centra la vista del canvas respecto a una escala dada
   * @param scale Escala a partir de la cual se desea centrar la vista del canvas
   */
  public centerViewPort(scale: number): void {
    const center = this.canvas.getCenter();
    this.canvas.setViewportTransform([1, 0, 0, 1, 0, 0]);
    this.canvas.zoomToPoint(new fabric.Point(center?.left as number, center?.top as number), scale);
    this.canvas.requestRenderAll();
  }

  /**
   * Permite arrastrar la vista actual del canvas o hacer una vista panorámica de este mediante el arrastre del mismo
   *
   * @param isDragMode Activa o desactiva el modo de arrastre
   */
  public toggleDragMode(isDragMode: boolean): void {
    // Mantiene el seguimiento del estado del modo arrastrar
    let state = STATE_IDLE;

    if (isDragMode) {
      // Descarta cualquier objeto seleccionado
      this.canvas.discardActiveObject();
      // Cambia el icono del cursor al icono 'move'
      this.canvas.defaultCursor = 'move';
      // Recorre todos los objetos en el canvas y deshabilita sus eventos y que se puedan seleccionar
      // Los valores almacenados en evento y seleccione se almacenan temporalmente en otros campos del mismo objeto sin interferir con este
      this.canvas.forEachObject(function (object: any) {
        object.prevEvented = object.evented;
        object.prevSelectable = object.selectable;
        object.evented = false;
        object.selectable = false;
      });

      // Cuando la presión sobre clic el mouse se libera, se cambia el estado del cambas a IDLE
      this.canvas.on('mouse:up', function (e) {
        state = STATE_IDLE;
      });

      // Cuando la presión sobre el mouse se produce, se establece el estado del panning
      this.canvas.on('mouse:down', (e: any) => {
        if (e.e.clientX && e.e.clientY) {
          state = STATE_PANNING;
          // @ts-ignore
          this.canvas.lastClientX = e.e.clientX;
          // @ts-ignore
          this.canvas.lastClientY = e.e.clientY;
        }
      });

      //Cuando se mueve el arrastre, se recalcula la posición del contenido del canvas dentro del viewport
      this.canvas.on('mouse:move', (e: any) => {
        if (state === STATE_PANNING && e && e.e && !e.e.touches) {
          this.canvas.selection = false;

          // Calcula deltas
          let deltaX = 0;
          let deltaY = 0;
          // @ts-ignore
          if (this.canvas.lastClientX) {
            // @ts-ignore
            deltaX = e.e.clientX - this.canvas.lastClientX;
          }
          // @ts-ignore
          if (this.canvas.lastClientY) {
            // @ts-ignore
            deltaY = e.e.clientY - this.canvas.lastClientY;
          }
          // Actualiza los últimos valores de X y Y
          // @ts-ignore
          this.canvas.lastClientX = e.e.clientX;
          // @ts-ignore
          this.canvas.lastClientY = e.e.clientY;

          let delta = new fabric.Point(deltaX, deltaY);
          this.canvas.relativePan(delta);
        }
      });
    } else {
      // Cuando se sale del modo de arrastre, se reestablecen los valores previos de todos los objetos en el canvas
      this.canvas.forEachObject(function (object: any) {
        object.evented = object.prevEvented !== undefined ? object.prevEvented : object.evented;
        object.selectable =
          object.prevSelectable !== undefined ? object.prevSelectable : object.selectable;
      });
      //El icono del cursor regresa a era antes del activar el modo de arrastre
      this.canvas.defaultCursor = 'default';
      // Elimina los eventos activos
      this.canvas.off('mouse:up');
      this.canvas.off('mouse:down');
      this.canvas.off('mouse:move');
      // this.canvas.off('touch:drag');
      // Reestable la capacidad de seleccionar elementos dentro del canvas
      this.canvas.selection = true;

      state = STATE_IDLE;
    }
  }

  /**
   * Permite alejar la vista actual del canvas
   */
  public zoomOut(): void {
    const zoomOut = this.canvas.getZoom() - 0.05;
    const center = this.canvas.getCenter();
    const centerPoint = new fabric.Point(center.left, center.top);
    this.canvas.zoomToPoint(centerPoint, zoomOut);
    this.canvas.requestRenderAll();
  }

  /**
   * Permite acercar la vista actual del canvas
   */
  public zoomIn(): void {
    const zoomOut = this.canvas.getZoom() + 0.05;
    const center = this.canvas.getCenter();
    const centerPoint = new fabric.Point(center.left, center.top);
    this.canvas.zoomToPoint(centerPoint, zoomOut);
    this.canvas.requestRenderAll();
  }

  /**
   * Permite descargar todo el contenido dentro del área de trabajo del canvas
   */
  public async downloadCropImageFromCanvas(): Promise<string> {
    const bgWorkArea: any = this.canvas.backgroundImage as Image;

    const widthCanvas = this.canvas?.width as number;
    const heightCanvas = this.canvas?.height as number;
    const topCenter = (heightCanvas - bgWorkArea.height) / 2;
    const leftCenter = (widthCanvas - bgWorkArea.width) / 2;

    let objs = [
      this.canvas.backgroundImage as any,
      ...this.canvas.getObjects().filter((item, i) => i > 0),
    ];

    if (APP_CONF.SHOW_FOOTER_BPP) {
      const logoFooterImg = await (async () =>
        new Promise<any>((resolve) => {
          try {
            fabric.Image.fromURL(footerImg, (image) => {
              image.set({
                left: leftCenter + bgWorkArea.width - 360,
                top: topCenter + bgWorkArea.height - 52,
              });
              resolve(image);
            });
          } catch (e) {
            resolve(new fabric.Image());
          }
        }))();
      const bgFooter: Rect = new fabric.Rect({
        width: bgWorkArea.width + 1,
        height: 52,
        fill: '#255aa8',
        selectable: false,
        objectCaching: false,
        evented: false,
        dirty: true,
        left: leftCenter - 1,
        top: topCenter + bgWorkArea.height - 52,
      });

      objs.push(bgFooter, logoFooterImg);
    }

    const cloneObjects: any = objs.map((obj) => {
      return fabric.util.object.clone(obj);
    });
    const group: any = new fabric.Group(cloneObjects as any, {
      left: 0,
      top: 0,
      objectCaching: false
    });
    const workArea = cloneObjects[0];

    const url = group.toDataURL({
      quality: 1,
      format: 'JPG',
      left: cloneObjects[0].left + group.calcTransformMatrix()[4],
      top: cloneObjects[0].top + group.calcTransformMatrix()[5],
      width: workArea.width,
      height: workArea.height,
    });
    const a = document.createElement('a');
    a.href = url as string;
    a.download = `composicion_${Date.now()}.jpg`;
    a.click();

    return Promise.resolve(url);
  }

  /**
   * Lleva el objeto seleccionado hacia atrás en la pila de objetos dentro del canvas
   */
  sendBackwards() {
    const objects = this.canvas.getObjects();
    const activeObject = this.canvas.getActiveObject();
    const indexActiveObject = objects.indexOf(activeObject);
    if (activeObject && indexActiveObject > 1) {
      // Solo se llevan hacia atrás aquellos objetos que estén activos y que sun posición
      // en la lista de objetos no sea inferior al segundo objeto. Esto se debe a que
      // el primer objeto en la lista representa el área de trabajo, por ello no puede
      //haber elementos antes que este, pues desaparecerían o quedarían detrás del área de trabajo.
      this.canvas.sendBackwards(activeObject);
    }
  }

  /**
   * Lleva el objeto seleccionado hacia adelante en la pila de objetos dentro del canvas
   */
  bringForward() {
    const activeObject = this.canvas.getActiveObject();
    if (activeObject) {
      this.canvas.bringForward(activeObject);
    }
  }

  /**
   * Agrega nueva imagen dentro del área de trabajo del canvas
   * @param srcImage
   */
  addImageElem(srcImage: string) {
    const workArea = this.canvas.backgroundImage as Rect;
    fabric.Image.fromURL(srcImage, (image) => {
      image.set({
        left: workArea.left,
        top: workArea.top,
        objectCaching: false,
        globalCompositeOperation: 'source-atop',
      });
      this.canvas.add(image);
      this.canvas.setActiveObject(image);
    });
  }

  /**
   * Agrega nuevo texto dentro del área de trabajo del canvas con un formato dado
   * @param textFormat
   */
  public addTextElem(textFormat: TextFormatSettings) {
    const workArea = this.canvas.backgroundImage as Rect;
    const text = new fabric.IText('Escribe aquí', {
      fontFamily: textFormat.fontFamily,
      textAlign: textFormat.textAlign,
      fontStyle: textFormat.fontStyle,
      fontWeight: textFormat.bold ? 'bold' : undefined,
      fill: textFormat.color,
      padding: 20,
      globalCompositeOperation: 'source-atop',
    });
    // @ts-ignore
    text.left = workArea.left + (workArea.width - text.width) / 2;
    // @ts-ignore
    text.top = workArea.top + (workArea.height - text.height) / 2;
    this.canvas.add(text);
    this.canvas.setActiveObject(text);
  }
}
